if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          pagination: this.querySelector('.product__media-pagination'),
          layeredPreviewPagination: this.querySelector('[data-layered-preview-pagination]'),
          layeredPreviewThumbnail: this.querySelector('[data-layered-preview-thumbnail]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.thumbnailRailMql = window.matchMedia('(min-width: 990px)');
        this.layeredPreviewMapElement = this.querySelector('[data-layered-preview-map]');

        if (this.layeredPreviewMapElement) this.initializeLayeredPreview();
        if (this.dataset.desktopLayout === 'thumbnail_rail') this.initializeThumbnailRailWheelNavigation();

        if (this.elements.pagination) {
          this.elements.viewer.addEventListener('slideChanged', this.onPaginationSlideChanged.bind(this));
          this.elements.pagination.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch.addEventListener(
              'click',
              this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
            );
          });
        }

        if (!this.elements.thumbnails) return;

        const handleSlideChanged = this.onSlideChanged.bind(this);
        const thumbnailSlideChanged =
          this.dataset.desktopLayout === 'thumbnail_rail'
            ? handleSlideChanged
            : debounce(handleSlideChanged, 500);
        this.elements.viewer.addEventListener('slideChanged', thumbnailSlideChanged);
        this.elements.thumbnails
          .querySelectorAll('[data-target]:not([data-layered-preview-thumbnail])')
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
        if (
          this.dataset.desktopLayout !== 'thumbnail_rail' &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      disconnectedCallback() {
        this.detachThumbnailRailWheelNavigation();
        clearTimeout(this.thumbnailRailWheelResetTimer);
        if (this.handleThumbnailRailWheelViewportChangeBound) {
          this.thumbnailRailMql.removeEventListener('change', this.handleThumbnailRailWheelViewportChangeBound);
        }

        if (this.layeredPreviewData) {
          this.layeredPreviewRequestId += 1;
          cancelAnimationFrame(this.layeredPreviewInteractionFrame);
          cancelAnimationFrame(this.layeredPreviewEnterFrame);
          this.layeredPreviewSlide?.classList.remove('is-active');
          this.layeredPreviewSlide?.setAttribute('aria-hidden', 'true');
          if (this.layeredPreviewSlide) this.layeredPreviewSlide.hidden = true;
          if (this.elements.layeredPreviewPagination) this.elements.layeredPreviewPagination.hidden = true;
          if (this.elements.layeredPreviewThumbnail) this.elements.layeredPreviewThumbnail.hidden = true;
          this.elements.viewer.update?.();
          this.productInfo?.removeEventListener('change', this.handleLayeredPreviewChangeBound);
          this.productInfo?.removeEventListener('click', this.handleLayeredPreviewClickBound);
          this.productInfo?.removeEventListener('pointerover', this.handleLayeredPreviewWarmInteractionBound);
          this.productInfo?.removeEventListener('pointerdown', this.handleLayeredPreviewWarmInteractionBound);
          this.productInfo?.removeEventListener('focusin', this.handleLayeredPreviewWarmInteractionBound);
          this.elements.layeredPreviewPagination?.removeEventListener(
            'click',
            this.handleLayeredPreviewPaginationClickBound
          );
          this.elements.layeredPreviewThumbnail
            ?.querySelector('button')
            ?.removeEventListener('click', this.handleLayeredPreviewThumbnailClickBound);
          this.thumbnailRailMql.removeEventListener('change', this.handleThumbnailRailViewportChangeBound);
        }
      }

      initializeLayeredPreview() {
        try {
          this.layeredPreviewData = JSON.parse(this.layeredPreviewMapElement.textContent);
        } catch (error) {
          this.layeredPreviewData = null;
          return;
        }

        this.productInfo = this.closest('product-info');
        this.layeredPreviewSlide = this.querySelector('[data-layered-preview-slide]');
        this.layeredPreviewLayers = {
          background: this.querySelectorAll('[data-layered-preview-background]'),
          envelope: this.querySelectorAll('[data-layered-preview-envelope]'),
          card: this.querySelectorAll('[data-layered-preview-card]'),
          overlay: this.querySelectorAll('[data-layered-preview-overlay]'),
        };
        this.handleLayeredPreviewChangeBound = this.handleLayeredPreviewChange.bind(this);
        this.handleLayeredPreviewClickBound = this.handleLayeredPreviewClick.bind(this);
        this.handleLayeredPreviewWarmInteractionBound = this.handleLayeredPreviewWarmInteraction.bind(this);
        this.handleLayeredPreviewPaginationClickBound = this.handleLayeredPreviewPaginationClick.bind(this);
        this.handleLayeredPreviewThumbnailClickBound = () => this.focusLayeredPreview('auto');
        this.handleThumbnailRailViewportChangeBound = this.handleThumbnailRailViewportChange.bind(this);
        this.productInfo?.addEventListener('change', this.handleLayeredPreviewChangeBound);
        this.productInfo?.addEventListener('click', this.handleLayeredPreviewClickBound);
        this.productInfo?.addEventListener('pointerover', this.handleLayeredPreviewWarmInteractionBound);
        this.productInfo?.addEventListener('pointerdown', this.handleLayeredPreviewWarmInteractionBound);
        this.productInfo?.addEventListener('focusin', (event) => {
          this.handleLayeredPreviewWarmInteraction(event);
          const card = event.target?.closest?.('[data-personalisation-card]');
          if (card) {
            const cardSwatch = card.querySelector('.envelope-colour-swatch.is-active');
            const colour = cardSwatch?.dataset?.colourName || cardSwatch?.dataset?.colour;
            if (colour && colour !== this.lastInteractedColour) {
              this.lastInteractedColour = colour;
              this.queueLayeredPreviewUpdate();
            }
          }
        });
        this.elements.layeredPreviewPagination?.addEventListener(
          'click',
          this.handleLayeredPreviewPaginationClickBound
        );
        this.elements.layeredPreviewThumbnail
          ?.querySelector('button')
          ?.addEventListener('click', this.handleLayeredPreviewThumbnailClickBound);
        this.thumbnailRailMql.addEventListener('change', this.handleThumbnailRailViewportChangeBound);
        this.layeredPreviewImages = new Map();
        this.layeredPreviewRequestId = 0;
        this.layeredPreviewRequested = false;
        this.lastInteractedColour = null;
        this.preloadInitialBoardingPreviewImages();

        if (this.layeredPreviewData?.mode === 'name-envelope') {
          this.filterMediaByOccasion();
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.mediaVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (this.layeredPreviewData?.mode === 'name-envelope') {
              this.filterMediaByOccasion();
            }
          });
          this.mediaOptionUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
            if (this.layeredPreviewData?.mode === 'name-envelope') {
              const optionName = (event?.data?.target?.name || event?.data?.target?.dataset?.optionName || '').toLowerCase();
              const isOccasion = optionName.includes('occasion') || !optionName;
              setTimeout(() => this.filterMediaByOccasion(isOccasion), 50);
            }
          });
        }

        if (new URLSearchParams(window.location.search).has('edit_line')) {
          this.layeredPreviewRequested = true;
          this.updateLayeredPreview();
        }
      }

      handleLayeredPreviewChange(event) {
        if (!event.isTrusted) return;
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        const card = target.closest('[data-personalisation-card]');
        if (card) {
          const cardSwatch = card.querySelector('.envelope-colour-swatch.is-active, .boarding-colour-swatch.is-active');
          const colour = cardSwatch?.dataset?.colourName || cardSwatch?.dataset?.colour;
          if (colour) {
            this.lastInteractedColour = colour;
          }
        }
        if (this.layeredPreviewData?.mode === 'name-envelope') {
          const isOccasionChange = Boolean(target.matches('select[name*="Occasion"], input[name*="Occasion"], select[name*="occasion"], input[name*="occasion"]'));
          this.filterMediaByOccasion(isOccasionChange);
        }
        if (
          !target.matches(
            '.boarding-colour-input, .boarding-envelope-input, [data-boarding-variant="personalisation"] input, .envelope-colour-swatch, .boarding-colour-swatch, input[name*="Colour"], input[name*="Color"], input[name*="Occasion"], select[name*="Occasion"], input[data-personalisation-name], input[name*="properties"]'
          )
        ) {
          return;
        }
        this.queueLayeredPreviewUpdate();
      }

      handleLayeredPreviewClick(event) {
        if (!event.isTrusted || !(event.target instanceof Element)) return;
        const swatch = event.target.closest('.envelope-colour-swatch, .boarding-colour-swatch');
        if (swatch) {
          const colour = swatch.dataset.colourName || swatch.dataset.colour;
          if (colour) {
            this.lastInteractedColour = colour;
          }
        } else {
          const card = event.target.closest('[data-personalisation-card]');
          if (card) {
            const cardSwatch = card.querySelector('.envelope-colour-swatch.is-active, .boarding-colour-swatch.is-active');
            const colour = cardSwatch?.dataset?.colourName || cardSwatch?.dataset?.colour;
            if (colour) {
              this.lastInteractedColour = colour;
            }
          }
        }
        if (this.layeredPreviewData?.mode === 'name-envelope') {
          this.filterMediaByOccasion();
        }
        if (
          !event.target.closest(
            '.boarding-colour-input, .boarding-colour-swatch, .boarding-envelope-input, .boarding-envelope-option, [data-boarding-variant="personalisation"] input, [data-boarding-variant="personalisation"] label, .envelope-colour-swatch, [data-personalisation-card]'
          )
        ) {
          return;
        }
        this.queueLayeredPreviewUpdate();
      }

      filterMediaByOccasion(autoSelectFirst = false) {
        if (this.layeredPreviewData?.mode !== 'name-envelope') return;

        const occasionSelect = this.productInfo?.querySelector(
          'select[name*="Occasion"], select[name*="occasion"], input[name*="Occasion"]:checked, input[name*="occasion"]:checked'
        );
        const selectedOccasion = (occasionSelect?.value || '').trim().toLowerCase();
        let cleanOccasion = '';
        if (selectedOccasion.includes('birthday')) cleanOccasion = 'birthday';
        else if (selectedOccasion.includes('christmas')) cleanOccasion = 'christmas';
        else if (selectedOccasion.includes('name')) cleanOccasion = 'name';

        if (!cleanOccasion) {
          const path = window.location.pathname.toLowerCase();
          if (path.includes('christmas')) cleanOccasion = 'christmas';
          else if (path.includes('birthday')) cleanOccasion = 'birthday';
          else cleanOccasion = 'name';
        }

        const normalise = (val) => (val || '').trim().toLowerCase();

        const allSlides = this.querySelectorAll('.product__media-list > .product__media-item:not([data-layered-preview-slide])');
        const allThumbs = this.querySelectorAll('.thumbnail-list > .thumbnail-list__item:not([data-layered-preview-thumbnail])');

        let visibleCount = 0;
        let firstMatchSlide = null;

        allSlides.forEach((slide) => {
          const alt = normalise(slide.dataset.mediaAlt || slide.querySelector('img')?.alt || '');
          const isBirthday = alt.includes('birthday');
          const isChristmas = alt.includes('christmas');
          const isName = alt.includes('name');

          let isMatch = true;
          if (cleanOccasion === 'birthday') {
            isMatch = isBirthday || (!isChristmas && !isName);
          } else if (cleanOccasion === 'christmas') {
            isMatch = isChristmas || (!isBirthday && !isName);
          } else if (cleanOccasion === 'name') {
            isMatch = isName || (!isBirthday && !isChristmas);
          }

          slide.style.display = isMatch ? '' : 'none';
          slide.classList.toggle('is-hidden-by-occasion', !isMatch);
          if (isMatch) {
            visibleCount++;
            if (!firstMatchSlide) {
              firstMatchSlide = slide;
            }
          }
        });

        allThumbs.forEach((thumb) => {
          const alt = normalise(thumb.dataset.mediaAlt || thumb.querySelector('img')?.alt || '');
          const isBirthday = alt.includes('birthday');
          const isChristmas = alt.includes('christmas');
          const isName = alt.includes('name');

          let isMatch = true;
          if (cleanOccasion === 'birthday') {
            isMatch = isBirthday || (!isChristmas && !isName);
          } else if (cleanOccasion === 'christmas') {
            isMatch = isChristmas || (!isBirthday && !isName);
          } else if (cleanOccasion === 'name') {
            isMatch = isName || (!isBirthday && !isChristmas);
          }

          thumb.style.display = isMatch ? '' : 'none';
          thumb.classList.toggle('is-hidden-by-occasion', !isMatch);
        });

        const allDots = this.querySelectorAll('.product__media-pagination-dot:not([data-layered-preview-pagination])');
        allDots.forEach((dot) => {
          const targetId = dot.dataset.target;
          let isMatch = true;
          if (targetId) {
            const matchingSlide = this.querySelector(`li[data-media-id="${targetId}"]`);
            if (matchingSlide) {
              isMatch = matchingSlide.style.display !== 'none';
            } else {
              const alt = normalise(dot.dataset.mediaAlt || '');
              const isBirthday = alt.includes('birthday');
              const isChristmas = alt.includes('christmas');
              const isName = alt.includes('name');

              if (cleanOccasion === 'birthday') {
                isMatch = isBirthday || (!isChristmas && !isName);
              } else if (cleanOccasion === 'christmas') {
                isMatch = isChristmas || (!isBirthday && !isName);
              } else if (cleanOccasion === 'name') {
                isMatch = isName || (!isBirthday && !isChristmas);
              }
            }
          }
          dot.style.display = isMatch ? '' : 'none';
          dot.classList.toggle('is-hidden-by-occasion', !isMatch);
        });

        const totalCounter = this.querySelector('.slider-counter--total');
        if (totalCounter && visibleCount > 0) {
          totalCounter.textContent = visibleCount;
        }

        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        this.elements.viewer?.update?.();

        if (autoSelectFirst && firstMatchSlide?.dataset?.mediaId) {
          this.setActiveMedia(firstMatchSlide.dataset.mediaId, false);
        }
      }

      queueLayeredPreviewUpdate() {
        this.layeredPreviewRequested = true;
        this.layeredPreviewRequestId += 1;
        cancelAnimationFrame(this.layeredPreviewInteractionFrame);
        this.layeredPreviewInteractionFrame = requestAnimationFrame(() => {
          this.layeredPreviewInteractionFrame = null;
          this.updateLayeredPreview();
        });
      }

      handleLayeredPreviewWarmInteraction(event) {
        if (!(event.target instanceof Element)) return;

        const colourSwatch = event.target.closest('.boarding-colour-swatch, .envelope-colour-swatch');
        const envOption = event.target.closest('.boarding-envelope-option');
        const persOption = event.target.closest('.boarding-personalisation-option');
        const directInput = event.target.closest(
          '.boarding-colour-input, .boarding-envelope-input, [data-boarding-variant="personalisation"] input'
        );

        const normalise = (value) => (value || '').trim().toLocaleLowerCase();
        const activeCard = this.productInfo?.querySelector('[data-personalisation-card].is-expanded')
          || this.productInfo?.querySelector('[data-personalisation-card]:not(.is-collapsed)')
          || this.productInfo?.querySelector('[data-personalisation-card]');

        let colour = colourSwatch?.dataset?.colourName || colourSwatch?.dataset?.colour
          || activeCard?.querySelector('[data-card-colour-hidden-input]')?.value
          || this.productInfo?.querySelector('.boarding-colour-input:checked')?.value
          || 'Gold';
        let envelopeName = envOption?.dataset?.envelopeName
          || activeCard?.querySelector('[data-envelope-hidden-input]')?.value
          || this.productInfo?.querySelector('.boarding-envelope-input:checked')?.value
          || this.productInfo?.querySelector('.boarding-envelope-input')?.value
          || this.layeredPreviewData?.envelopes?.[0]?.name
          || '';
        let personalisation = persOption?.dataset?.personalisationName
          || activeCard?.querySelector('[data-envelope-personalisation-hidden-input]')?.value
          || this.productInfo?.querySelector('[data-boarding-variant="personalisation"] input:checked')?.value
          || this.productInfo?.querySelector('[data-boarding-variant="personalisation"] input')?.value
          || '';

        if (directInput?.matches('.boarding-colour-input')) colour = directInput.value;
        else if (directInput?.matches('.boarding-envelope-input')) envelopeName = directInput.value;
        else if (directInput) personalisation = directInput.value;

        const card = this.layeredPreviewData.cards.find(
          (item) => normalise(item.colour) === normalise(colour)
        ) || this.layeredPreviewData.cards[0];
        const envelope = this.findLayeredPreviewEnvelope(envelopeName);
        const hasEnvelope = envelope?.image && normalise(envelope.group) !== 'none';
        const isPremiumEnvelope = this.layeredPreviewData.mode === 'premium-envelope';
        const overlay =
          hasEnvelope && normalise(personalisation).includes('with name')
            ? this.findLayeredPreviewOverlay(envelope, colour, isPremiumEnvelope)
            : null;

        const sources =
          isPremiumEnvelope
            ? [this.layeredPreviewData.background, hasEnvelope ? envelope.image : null, overlay?.image]
            : [this.layeredPreviewData.background, card?.image, hasEnvelope ? envelope.image : null, overlay?.image];

        sources
          .filter(Boolean)
          .forEach((source) => this.preloadLayeredPreviewImage(source, 'high').catch(() => {}));
      }

      async updateLayeredPreview(options = {}) {
        if (!this.layeredPreviewData || !this.layeredPreviewSlide) return;
        const requestId = ++this.layeredPreviewRequestId;

        const normalise = (value) => (value || '').trim().toLocaleLowerCase();

        if (this.layeredPreviewData.mode === 'name-envelope') {
          let selectedColour = this.lastInteractedColour;
          if (!selectedColour) {
            const activeSwatch = this.productInfo?.querySelector(
              '[data-personalisation-card="1"] .envelope-colour-swatch.is-active, .envelope-colour-swatch.is-active'
            );
            const colourInput = this.productInfo?.querySelector(
              'input[name="properties[Envelope 1: Colour]"], input[name="properties[Colour]"], input[name*="Colour"], select[name*="Colour"]'
            );
            selectedColour = activeSwatch?.dataset?.colour || activeSwatch?.dataset?.colourName || colourInput?.value || 'Black';
          }
          const occasionSelect = this.productInfo?.querySelector(
            'select[name*="Occasion"], select[name*="occasion"], input[name*="Occasion"]:checked, input[name*="occasion"]:checked'
          );
          const selectedOccasion = occasionSelect?.value || '';

          const normColour = normalise(selectedColour);
          const normOccasion = normalise(selectedOccasion);
          const layers = this.layeredPreviewData.layers || [];
          let matchedLayer = layers.find((layer) => {
            const layerColour = normalise(layer.colour);
            const layerOccasion = normalise(layer.occasion);
            const colourMatches = layerColour === normColour;
            if (!colourMatches) return false;
            if (!normOccasion) return true;
            const cleanOccasion = normOccasion.replace('cash', '').replace('envelope', '').trim();
            return layerOccasion.includes(cleanOccasion);
          });

          if (!matchedLayer) {
            matchedLayer = layers.find((layer) => normalise(layer.colour) === normColour);
          }

          if (!matchedLayer?.image) return;

          try {
            await this.preloadLayeredPreviewImage(matchedLayer.image);
          } catch (error) {
            console.error('Layered preview image failed to load', error);
            return;
          }

          if (requestId !== this.layeredPreviewRequestId || !this.isConnected) return;

          this.setLayeredPreviewImage('background', this.layeredPreviewData.background);
          this.setLayeredPreviewImage('card', null);
          this.setLayeredPreviewImage('envelope', null);
          this.setLayeredPreviewImage('overlay', matchedLayer.image);
          this.setLayeredPreviewCartProperties({
            mode: 'name-envelope',
            background: this.layeredPreviewData.background,
            overlay: matchedLayer.image,
          });

          this.showLayeredPreview(requestId);
          return;
        }

        const activeCard = this.productInfo?.querySelector('[data-personalisation-card].is-expanded')
          || this.productInfo?.querySelector('[data-personalisation-card]:not(.is-collapsed)')
          || this.productInfo?.querySelector('[data-personalisation-card]');

        const colour = (typeof options === 'object' && options?.colour)
          || activeCard?.querySelector('[data-card-colour-hidden-input]')?.value
          || this.productInfo?.querySelector('.boarding-colour-input:checked')?.value
          || this.layeredPreviewData?.cards?.[0]?.colour
          || 'Gold';
        const envelope = (typeof options === 'object' && options?.envelope)
          || activeCard?.querySelector('[data-envelope-hidden-input]')?.value
          || this.productInfo?.querySelector('.boarding-envelope-input:checked')?.value
          || this.productInfo?.querySelector('.boarding-envelope-input')?.value
          || this.layeredPreviewData?.envelopes?.[0]?.name
          || '';
        const personalisation = (typeof options === 'object' && options?.personalisation)
          || activeCard?.querySelector('[data-envelope-personalisation-hidden-input]')?.value
          || this.productInfo?.querySelector('[data-boarding-variant="personalisation"] input:checked')?.value
          || this.productInfo?.querySelector('[data-boarding-variant="personalisation"] input')?.value
          || '';
        const card = this.layeredPreviewData.cards.find(
          (item) => normalise(item.colour) === normalise(colour)
        ) || this.layeredPreviewData.cards[0];
        const envelopeLayer = this.findLayeredPreviewEnvelope(envelope);
        const hasEnvelope = envelopeLayer?.image && normalise(envelopeLayer.group) !== 'none';
        const withName = normalise(personalisation).includes('with name');
        const isPremiumEnvelope = this.layeredPreviewData.mode === 'premium-envelope';
        const overlay = hasEnvelope && withName
          ? this.findLayeredPreviewOverlay(envelopeLayer, colour, isPremiumEnvelope)
          : null;

        if (isPremiumEnvelope && !hasEnvelope) {
          this.setLayeredPreviewImage('background', null);
          this.setLayeredPreviewImage('envelope', null);
          this.setLayeredPreviewImage('overlay', null);
          this.setLayeredPreviewCartProperties(null);
          this.exitLayeredPreview();
          return;
        }
        if (!isPremiumEnvelope && !card?.image) return;

        if (isPremiumEnvelope) {
          this.setLayeredPreviewImage('background', this.layeredPreviewData.background);
          this.setLayeredPreviewImage('envelope', envelopeLayer.image);
          this.setLayeredPreviewImage('card', null);
          this.setLayeredPreviewImage('overlay', overlay?.image);
          this.setLayeredPreviewCartProperties({
            mode: this.layeredPreviewData.mode,
            background: this.layeredPreviewData.background,
            envelope: envelopeLayer.image,
            overlay: overlay?.image,
          });
          this.showLayeredPreview(requestId);
          return;
        }

        const sources = [
          this.layeredPreviewData.background,
          hasEnvelope ? envelopeLayer.image : null,
          card.image,
          overlay?.image,
        ].filter(Boolean);

        try {
          await Promise.all(sources.map((source) => this.preloadLayeredPreviewImage(source)));
        } catch (error) {
          console.error('Layered preview image failed to load', error);
          return;
        }

        if (requestId !== this.layeredPreviewRequestId || !this.isConnected) return;

        this.setLayeredPreviewImage('background', this.layeredPreviewData.background);
        this.setLayeredPreviewImage('envelope', hasEnvelope ? envelopeLayer.image : null);
        this.setLayeredPreviewImage('card', card.image);
        this.setLayeredPreviewImage('overlay', overlay?.image);

        this.layeredPreviewSlide?.classList.toggle('is-no-envelope', !hasEnvelope);
        this.elements.layeredPreviewThumbnail?.classList.toggle('is-no-envelope', !hasEnvelope);
        this.querySelectorAll('.layered-preview-canvas').forEach((canvas) => {
          canvas.classList.toggle('is-no-envelope', !hasEnvelope);
        });

        this.setLayeredPreviewCartProperties({
          mode: this.layeredPreviewData.mode,
          background: this.layeredPreviewData.background,
          envelope: hasEnvelope ? envelopeLayer.image : null,
          overlay: overlay?.image,
          card: card.image,
        });

        this.showLayeredPreview(requestId);
      }

      showLayeredPreview(requestId) {
        cancelAnimationFrame(this.layeredPreviewEnterFrame);
        this.layeredPreviewSlide.hidden = false;
        this.layeredPreviewSlide.setAttribute('aria-hidden', 'false');
        const activate = () => {
          this.layeredPreviewEnterFrame = null;
          if (requestId !== this.layeredPreviewRequestId || !this.isConnected) {
            this.cancelLayeredPreviewEntrance();
            return;
          }

          this.activateLayeredPreview();
        };

        if (
          this.dataset.desktopLayout === 'thumbnail_rail' ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) {
          activate();
        } else {
          this.layeredPreviewEnterFrame = requestAnimationFrame(activate);
        }
      }

      activateLayeredPreview() {
        if (!this.layeredPreviewSlide) return;
        this.layeredPreviewSlide.hidden = false;
        this.layeredPreviewSlide.setAttribute('aria-hidden', 'false');
        this.layeredPreviewPeerVisible = true;
        if (this.elements.layeredPreviewPagination) this.elements.layeredPreviewPagination.hidden = false;
        if (this.elements.layeredPreviewThumbnail) this.elements.layeredPreviewThumbnail.hidden = false;
        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        requestAnimationFrame(() => this.focusLayeredPreview('auto'));
      }

      focusLayeredPreview(behavior = 'auto') {
        if (!this.layeredPreviewPeerVisible || !this.layeredPreviewSlide?.isConnected) return;
        this.elements.viewer.slider
          .querySelectorAll('li[data-media-id], li[data-layered-preview-slide]')
          .forEach((slide) => slide.classList.toggle('is-active', slide === this.layeredPreviewSlide));
        this.setActiveThumbnail(this.elements.layeredPreviewThumbnail);
        this.setActiveLayeredPreviewPagination();
        this.elements.viewer.slider.scrollTo({ left: this.layeredPreviewSlide.offsetLeft, behavior });
      }

      handleLayeredPreviewPaginationClick() {
        this.focusLayeredPreview('smooth');
      }

      setActiveLayeredPreviewPagination() {
        if (!this.elements.pagination || !this.elements.layeredPreviewPagination) return;
        this.elements.pagination.querySelectorAll('.product__media-pagination-dot').forEach((dot) => {
          const isPreview = dot === this.elements.layeredPreviewPagination;
          dot.classList.toggle('is-active', isPreview);
          if (isPreview) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        this.keepActivePaginationVisible(this.elements.layeredPreviewPagination);
      }

      initializeThumbnailRailWheelNavigation() {
        this.handleThumbnailRailWheelNavigationBound = this.handleThumbnailRailWheelNavigation.bind(this);
        this.handleThumbnailRailWheelViewportChangeBound = () => {
          if (this.thumbnailRailMql.matches) this.attachThumbnailRailWheelNavigation();
          else this.detachThumbnailRailWheelNavigation();
        };
        this.thumbnailRailMql.addEventListener('change', this.handleThumbnailRailWheelViewportChangeBound);
        this.handleThumbnailRailWheelViewportChangeBound();
      }

      attachThumbnailRailWheelNavigation() {
        if (this.thumbnailRailWheelNavigationAttached || !this.elements.viewer?.slider) return;
        this.thumbnailRailWheelNavigationAttached = true;
        this.elements.viewer.slider.addEventListener('wheel', this.handleThumbnailRailWheelNavigationBound, {
          passive: false,
        });
      }

      detachThumbnailRailWheelNavigation() {
        if (!this.thumbnailRailWheelNavigationAttached) return;
        this.thumbnailRailWheelNavigationAttached = false;
        this.thumbnailRailWheelLocked = false;
        this.thumbnailRailWheelDelta = 0;
        this.thumbnailRailWheelLastEventAt = 0;
        this.thumbnailRailWheelDirection = 0;
        this.thumbnailRailWheelNavigationAt = 0;
        this.thumbnailRailWheelTailMagnitude = Infinity;
        clearTimeout(this.thumbnailRailWheelResetTimer);
        this.elements.viewer?.slider?.removeEventListener('wheel', this.handleThumbnailRailWheelNavigationBound);
      }

      handleThumbnailRailWheelNavigation(event) {
        if (!this.isThumbnailRailDesktop()) return;
        const unit = event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? this.elements.viewer.slider.clientWidth
            : 1;
        const horizontalDelta = event.deltaX * unit;
        const verticalDelta = event.deltaY * unit;
        const horizontalMagnitude = Math.abs(horizontalDelta);
        const verticalMagnitude = Math.abs(verticalDelta);
        if (horizontalMagnitude < 0.5 || horizontalMagnitude < verticalMagnitude * 0.55) return;

        event.preventDefault();
        const eventTime = event.timeStamp;
        const eventDirection = horizontalDelta > 0 ? 1 : -1;
        const timeSinceNavigation = eventTime - (this.thumbnailRailWheelNavigationAt || 0);
        const gestureStarted =
          !this.thumbnailRailWheelLastEventAt || eventTime - this.thumbnailRailWheelLastEventAt > 90;
        const directionReversed =
          this.thumbnailRailWheelLocked &&
          eventDirection !== this.thumbnailRailWheelDirection &&
          timeSinceNavigation > 90 &&
          horizontalMagnitude >= 8;
        const gestureAccelerated =
          this.thumbnailRailWheelLocked &&
          eventDirection === this.thumbnailRailWheelDirection &&
          timeSinceNavigation > 200 &&
          Number.isFinite(this.thumbnailRailWheelTailMagnitude) &&
          this.thumbnailRailWheelTailMagnitude <= 2 &&
          horizontalMagnitude >= 10 &&
          horizontalMagnitude > this.thumbnailRailWheelTailMagnitude * 4;
        this.thumbnailRailWheelLastEventAt = eventTime;
        if (gestureStarted || directionReversed || gestureAccelerated) {
          this.thumbnailRailWheelLocked = false;
          this.thumbnailRailWheelDelta = 0;
          this.thumbnailRailWheelTailMagnitude = Infinity;
        }

        clearTimeout(this.thumbnailRailWheelResetTimer);
        this.thumbnailRailWheelResetTimer = setTimeout(() => {
          this.thumbnailRailWheelLocked = false;
          this.thumbnailRailWheelDelta = 0;
          this.thumbnailRailWheelLastEventAt = 0;
          this.thumbnailRailWheelDirection = 0;
          this.thumbnailRailWheelNavigationAt = 0;
          this.thumbnailRailWheelTailMagnitude = Infinity;
        }, 90);
        if (this.thumbnailRailWheelLocked) {
          if (timeSinceNavigation > 100) {
            this.thumbnailRailWheelTailMagnitude = Math.min(
              this.thumbnailRailWheelTailMagnitude,
              horizontalMagnitude
            );
          }
          return;
        }

        this.thumbnailRailWheelDelta = (this.thumbnailRailWheelDelta || 0) + horizontalDelta;
        if (Math.abs(this.thumbnailRailWheelDelta) < 8) return;
        const direction = this.thumbnailRailWheelDelta > 0 ? 1 : -1;
        this.thumbnailRailWheelDelta = 0;

        const gallerySlides = Array.from(
          this.elements.viewer.slider.querySelectorAll(
            'li[data-media-id]:not([style*="display: none"]):not([hidden]), li[data-layered-preview-slide]:not([hidden])'
          )
        );
        if (gallerySlides.length < 2) return;
        const activeIndex = gallerySlides.reduce((nearestIndex, slide, index) => {
          const nearestDistance = Math.abs(gallerySlides[nearestIndex].offsetLeft - this.elements.viewer.slider.scrollLeft);
          const slideDistance = Math.abs(slide.offsetLeft - this.elements.viewer.slider.scrollLeft);
          return slideDistance < nearestDistance ? index : nearestIndex;
        }, 0);
        const targetIndex = Math.min(
          gallerySlides.length - 1,
          Math.max(0, activeIndex + direction)
        );
        if (targetIndex === activeIndex) return;

        this.thumbnailRailWheelLocked = true;
        this.thumbnailRailWheelDirection = direction;
        this.thumbnailRailWheelNavigationAt = eventTime;
        this.thumbnailRailWheelTailMagnitude = Infinity;
        const targetSlide = gallerySlides[targetIndex];
        if (targetSlide === this.layeredPreviewSlide) {
          this.focusLayeredPreview('auto');
          return;
        }
        this.setActiveMedia(targetSlide.dataset.mediaId, false);
      }

      exitLayeredPreview() {
        this.layeredPreviewRequestId += 1;
        cancelAnimationFrame(this.layeredPreviewInteractionFrame);
        cancelAnimationFrame(this.layeredPreviewEnterFrame);
        this.layeredPreviewInteractionFrame = null;
        this.layeredPreviewEnterFrame = null;
        this.layeredPreviewSlide?.classList.remove('is-active');
        this.layeredPreviewSlide?.setAttribute('aria-hidden', 'true');

        if (this.layeredPreviewSlide) this.layeredPreviewSlide.hidden = true;
        this.layeredPreviewPeerVisible = false;
        if (this.elements.layeredPreviewPagination) {
          this.elements.layeredPreviewPagination.hidden = true;
          this.elements.layeredPreviewPagination.classList.remove('is-active');
          this.elements.layeredPreviewPagination.removeAttribute('aria-current');
        }
        if (this.elements.layeredPreviewThumbnail) this.elements.layeredPreviewThumbnail.hidden = true;
        this.elements.viewer.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
        const firstMedia = this.elements.viewer.slider?.querySelector('li[data-media-id]');
        if (firstMedia) {
          firstMedia.classList.add('is-active');
          this.setActivePagination(firstMedia.dataset.mediaId);
          requestAnimationFrame(() => {
            firstMedia.parentElement.scrollTo({ left: firstMedia.offsetLeft, behavior: 'auto' });
          });
        }
      }

      cancelLayeredPreviewEntrance() {
        if (!this.layeredPreviewSlide || this.layeredPreviewPeerVisible) return;
        this.layeredPreviewSlide.classList.remove('is-active');
        this.layeredPreviewSlide.setAttribute('aria-hidden', 'true');
        this.layeredPreviewSlide.hidden = true;
      }

      handleThumbnailRailViewportChange() {
        if (this.dataset.desktopLayout !== 'thumbnail_rail') return;

        const firstMedia = this.elements.viewer.slider?.querySelector('li[data-media-id]');
        this.elements.viewer.resetPages?.();
        const activeSlide =
          this.elements.viewer.slider?.querySelector(
            'li[data-layered-preview-slide]:not([hidden]).is-active, li[data-media-id].is-active'
          ) || firstMedia;
        if (activeSlide) {
          requestAnimationFrame(() => {
            activeSlide.parentElement.scrollTo({ left: activeSlide.offsetLeft, behavior: 'auto' });
            if (activeSlide === this.layeredPreviewSlide) {
              this.setActiveLayeredPreviewPagination();
              return;
            }
            this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${activeSlide.dataset.mediaId}"]`));
          });
        }
      }

      isThumbnailRailDesktop() {
        return this.dataset.desktopLayout === 'thumbnail_rail' && this.thumbnailRailMql.matches;
      }

      syncLayeredPreviewCartProperties() {
        if (!this.layeredPreviewData) {
          return;
        }
        if (
          this.layeredPreviewData.mode !== 'premium-envelope' &&
          this.layeredPreviewData.mode !== 'name-envelope' &&
          !this.layeredPreviewRequested &&
          !this.layeredPreviewPeerVisible
        ) {
          return;
        }

        const normalise = (value) => (value || '').trim().toLocaleLowerCase();

        if (this.layeredPreviewData.mode === 'name-envelope') {
          const bulkSelect = this.productInfo?.querySelector(
            'select[name*="Bulk"], select[name*="Discount"], select[name*="discount"], select[name*="Multi-buy"], input[name*="Bulk"]:checked, input[name*="Discount"]:checked'
          );
          const bulkVal = bulkSelect?.value || '';
          const match = bulkVal.match(/^(\d+)\s*(envelope|envelopes|piece|pieces|pcs)/i);
          const count = match ? parseInt(match[1], 10) : 1;
          if (count > 1) {
            this.setLayeredPreviewCartProperties(null);
            return;
          }

          const activeSwatch = this.productInfo?.querySelector(
            '[data-personalisation-card="1"] .envelope-colour-swatch.is-active, .envelope-colour-swatch.is-active'
          );
          const colourInput = this.productInfo?.querySelector(
            'input[name="properties[Envelope 1: Colour]"], input[name="properties[Colour]"], input[name*="Colour"], select[name*="Colour"]'
          );
          const selectedColour = activeSwatch?.dataset?.colour || activeSwatch?.dataset?.colourName || colourInput?.value || 'Black';
          const occasionSelect = this.productInfo?.querySelector(
            'select[name*="Occasion"], select[name*="occasion"], input[name*="Occasion"]:checked, input[name*="occasion"]:checked'
          );
          const selectedOccasion = occasionSelect?.value || '';

          const normColour = normalise(selectedColour);
          const normOccasion = normalise(selectedOccasion);
          const layers = this.layeredPreviewData.layers || [];
          let matchedLayer = layers.find((layer) => {
            const layerColour = normalise(layer.colour);
            const layerOccasion = normalise(layer.occasion);
            const colourMatches = layerColour === normColour;
            if (!colourMatches) return false;
            if (!normOccasion) return true;
            const cleanOccasion = normOccasion.replace('cash', '').replace('envelope', '').trim();
            return layerOccasion.includes(cleanOccasion);
          });
          if (!matchedLayer) {
            matchedLayer = layers.find((layer) => normalise(layer.colour) === normColour);
          }

          if (matchedLayer?.image) {
            this.setLayeredPreviewCartProperties({
              mode: 'name-envelope',
              background: this.layeredPreviewData.background,
              overlay: matchedLayer.image,
            });
          }
          return;
        }

        const colour = this.productInfo?.querySelector('.boarding-colour-input:checked')?.value;
        const envelopeName = this.productInfo?.querySelector('.boarding-envelope-input:checked')?.value;
        const personalisation = this.productInfo?.querySelector(
          '[data-boarding-variant="personalisation"] input:checked'
        )?.value;
        const card = this.layeredPreviewData.cards.find(
          (item) => normalise(item.colour) === normalise(colour)
        );

        const envelope = this.findLayeredPreviewEnvelope(envelopeName);
        const hasEnvelope = envelope?.image && normalise(envelope.group) !== 'none';
        const withName = normalise(personalisation).includes('with name');
        const isPremiumEnvelope = this.layeredPreviewData.mode === 'premium-envelope';

        if (isPremiumEnvelope) {
          this.setLayeredPreviewCartProperties(
            hasEnvelope
              ? {
                  mode: 'premium-envelope',
                  background: this.layeredPreviewData.background,
                  envelope: envelope.image,
                  overlay: withName ? this.findLayeredPreviewOverlay(envelope, colour, true)?.image : null,
                }
              : null
          );
          return;
        }

        if (!card?.image) {
          this.setLayeredPreviewCartProperties(null);
          return;
        }

        const overlay = hasEnvelope && withName ? this.findLayeredPreviewOverlay(envelope, colour) : null;

        this.setLayeredPreviewCartProperties({
          mode: 'boarding-card',
          background: this.layeredPreviewData.background,
          envelope: hasEnvelope ? envelope.image : null,
          overlay: overlay?.image,
          card: card.image,
        });
      }

      setLayeredPreviewCartProperties(layers) {
        const form = this.productInfo?.querySelector('product-form form');
        form?.querySelectorAll('[data-layered-preview-property]').forEach((input) => input.remove());
      }

      preloadInitialBoardingPreviewImages() {
        if (this.layeredPreviewData.mode === 'name-envelope') {
          const sources = [
            this.layeredPreviewData.background,
            ...(this.layeredPreviewData.layers || []).map((l) => l.image),
          ].filter(Boolean);
          sources.forEach((source) => this.preloadLayeredPreviewImage(source, 'low').catch(() => {}));
          return;
        }

        if (this.layeredPreviewData.mode !== 'boarding-card') return;

        const normalise = (value) => (value || '').trim().toLocaleLowerCase();
        const activeCard = this.productInfo?.querySelector('[data-personalisation-card]');
        const selectedColour =
          activeCard?.querySelector('[data-card-colour-hidden-input]')?.value ||
          this.productInfo?.querySelector('.boarding-colour-input:checked')?.value ||
          'Gold';
        const selectedEnvelopeName =
          activeCard?.querySelector('[data-envelope-hidden-input]')?.value ||
          this.productInfo?.querySelector('.boarding-envelope-input:checked')?.value ||
          this.productInfo?.querySelector('.boarding-envelope-input')?.value ||
          this.layeredPreviewData?.envelopes?.[0]?.name ||
          '';
        const selectedPersonalisation =
          activeCard?.querySelector('[data-envelope-personalisation-hidden-input]')?.value ||
          this.productInfo?.querySelector('[data-boarding-variant="personalisation"] input:checked')?.value ||
          this.productInfo?.querySelector('[data-boarding-variant="personalisation"] input')?.value ||
          '';

        const selectedCard = this.layeredPreviewData.cards.find(
          (item) => normalise(item.colour) === normalise(selectedColour)
        ) || this.layeredPreviewData.cards[0];
        const selectedEnvelope = this.findLayeredPreviewEnvelope(selectedEnvelopeName);
        const hasEnvelope = selectedEnvelope?.image && normalise(selectedEnvelope.group) !== 'none';
        const initialOverlay =
          hasEnvelope && normalise(selectedPersonalisation).includes('with name')
            ? this.findLayeredPreviewOverlay(selectedEnvelope, selectedCard?.colour)
            : null;

        [
          this.layeredPreviewData.background,
          selectedCard?.image,
          hasEnvelope ? selectedEnvelope.image : null,
          initialOverlay?.image,
        ]
          .filter(Boolean)
          .forEach((source) => this.preloadLayeredPreviewImage(source, 'high').catch(() => {}));

        const preloadAllAssets = () => {
          const allCards = (this.layeredPreviewData.cards || []).map((item) => item.image);
          const allEnvelopes = (this.layeredPreviewData.envelopes || []).map((item) => item.image);
          const allOverlays = (this.layeredPreviewData.overlays || []).map((item) => item.image);

          [...allCards, ...allEnvelopes, ...allOverlays]
            .filter(Boolean)
            .forEach((source) => this.preloadLayeredPreviewImage(source).catch(() => {}));
        };

        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(preloadAllAssets, { timeout: 800 });
        } else {
          window.setTimeout(preloadAllAssets, 200);
        }
      }

      preloadLayeredPreviewImage(source, priority = 'auto') {
        if (!source) return Promise.resolve();
        if (this.layeredPreviewImages.has(source)) {
          const resource = this.layeredPreviewImages.get(source);
          if (priority === 'high') resource.image.fetchPriority = 'high';
          return resource.promise;
        }

        const image = new Image();
        image.decoding = 'async';
        image.fetchPriority = priority;
        const promise = new Promise((resolve, reject) => {
          image.onload = () => {
            if (typeof image.decode !== 'function') {
              resolve();
              return;
            }
            image.decode().then(resolve).catch(resolve);
          };
          image.onerror = () => reject(new Error(source));
          image.src = source;
          if (image.complete && image.naturalWidth > 0) image.onload();
        });

        this.layeredPreviewImages.set(source, { image, promise });
        return promise;
      }

      normaliseLayeredPreviewEnvelopeName(value) {
        const normalised = (value || '')
          .trim()
          .toLocaleLowerCase()
          .replace(/\benv\.?\b/g, 'envelope')
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();

        if (normalised === 'none' || normalised.includes('no envelope')) return 'none';

        return normalised
          .split(/\s+/)
          .filter((token) => token && token !== 'classic' && token !== 'envelope')
          .sort()
          .join(' ');
      }

      findLayeredPreviewEnvelope(name) {
        const exactName = (name || '').trim().toLocaleLowerCase();
        const envelopes = this.layeredPreviewData?.envelopes || [];
        const exact = envelopes.find((item) => (item.name || '').trim().toLocaleLowerCase() === exactName);
        if (exact) return exact;

        const canonicalName = this.normaliseLayeredPreviewEnvelopeName(name);
        return envelopes.find(
          (item) => this.normaliseLayeredPreviewEnvelopeName(item.name) === canonicalName
        );
      }

      findLayeredPreviewOverlay(envelope, colour, allowAnyColour = false) {
        const normalise = (value) => (value || '').trim().toLocaleLowerCase();
        const sameEnvelope = (item) =>
          this.normaliseLayeredPreviewEnvelopeName(item.envelopeName) ===
          this.normaliseLayeredPreviewEnvelopeName(envelope.name);
        const sameColour = (item) => normalise(item.cardColour) === normalise(colour);
        const noColour = (item) => !normalise(item.cardColour);
        const goldColour = (item) => normalise(item.cardColour) === 'gold';
        const sameGroup = (item) =>
          !normalise(item.envelopeName) && normalise(item.envelopeGroup) === normalise(envelope.group);

        return (
          this.layeredPreviewData.overlays.find((item) => sameEnvelope(item) && sameColour(item)) ||
          this.layeredPreviewData.overlays.find((item) => sameEnvelope(item) && noColour(item)) ||
          this.layeredPreviewData.overlays.find((item) => sameGroup(item) && sameColour(item)) ||
          this.layeredPreviewData.overlays.find((item) => sameGroup(item) && noColour(item)) ||
          (allowAnyColour && this.layeredPreviewData.overlays.find((item) => sameEnvelope(item) && goldColour(item))) ||
          (allowAnyColour && this.layeredPreviewData.overlays.find((item) => sameEnvelope(item))) ||
          (allowAnyColour && this.layeredPreviewData.overlays.find((item) => sameGroup(item)))
        );
      }

      setLayeredPreviewImage(layer, source) {
        this.layeredPreviewLayers[layer]?.forEach((image) => {
          image.hidden = !source;
          if (source) image.src = source;
          else image.removeAttribute('src');
        });
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        this.elements.viewer.slider
          .querySelectorAll('li[data-media-id], li[data-layered-preview-slide]')
          .forEach((slide) => {
            slide.classList.toggle('is-active', slide === event.detail.currentElement);
          });
        if (event.detail.currentElement === this.layeredPreviewSlide) {
          this.setActiveThumbnail(this.elements.layeredPreviewThumbnail);
          return;
        }
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onPaginationSlideChanged(event) {
        if (!event.detail.currentElement) return;
        if (event.detail.currentElement === this.layeredPreviewSlide) {
          this.setActiveLayeredPreviewPagination();
          return;
        }
        const mediaId = event.detail.currentElement.dataset.mediaId;
        if (!mediaId) return;
        this.setActivePagination(mediaId);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`li[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('li[data-media-id]');
        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('li[data-media-id], li[data-layered-preview-slide]')
          .forEach((element) => element.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          requestAnimationFrame(() => {
            if (!this.mql.matches || this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({
                left: activeMedia.offsetLeft,
                behavior: 'auto',
              });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top, behavior: 'auto' });
          });
        });
        this.playActiveMedia(activeMedia);
        this.setActivePagination(mediaId);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActivePagination(mediaId) {
        if (!this.elements.pagination) return;

        if (this.elements.layeredPreviewPagination) {
          this.elements.layeredPreviewPagination.classList.remove('is-active');
          this.elements.layeredPreviewPagination.removeAttribute('aria-current');
        }
        let activeDot = null;
        this.elements.pagination.querySelectorAll('[data-target]').forEach((dot) => {
          const isActive = dot.dataset.target === mediaId;
          dot.classList.toggle('is-active', isActive);
          if (isActive) {
            activeDot = dot;
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        this.keepActivePaginationVisible(activeDot);
      }

      keepActivePaginationVisible(activeDot) {
        const pagination = this.elements.pagination;
        if (!activeDot || !pagination || this.thumbnailRailMql.matches || pagination.clientWidth === 0) return;

        const visibleStart = pagination.scrollLeft;
        const visibleEnd = visibleStart + pagination.clientWidth;
        const dotStart = activeDot.offsetLeft;
        const dotEnd = dotStart + activeDot.offsetWidth;
        if (dotStart >= visibleStart && dotEnd <= visibleEnd) return;

        const centeredPosition = dotStart - (pagination.clientWidth - activeDot.offsetWidth) / 2;
        const maximumPosition = Math.max(0, pagination.scrollWidth - pagination.clientWidth);
        pagination.scrollTo({
          left: Math.min(maximumPosition, Math.max(0, centeredPosition)),
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.isThumbnailRailDesktop()) {
          const thumbnailList = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          if (
            thumbnailTop >= thumbnailList.scrollTop + 10 &&
            thumbnailBottom <= thumbnailList.scrollTop + thumbnailList.clientHeight - 10
          ) {
            return;
          }

          thumbnailList.scrollTo({
            top: Math.max(0, thumbnailTop - (thumbnailList.clientHeight - thumbnail.offsetHeight) / 2),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
